import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { companyInfo, team } from '../data/mock';

const Contatti = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!privacyAccepted) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Devi accettare l\'informativa sulla privacy per procedere.' 
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          marketingConsent: marketingAccepted
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({ type: 'success', message: result.message });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setPrivacyAccepted(false);
        setMarketingAccepted(false);
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: result.detail || 'Errore durante l\'invio. Riprova più tardi.' 
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Errore di connessione. Verifica la tua connessione e riprova.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contatti-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container">
          <h1 className="display-title">Contattaci</h1>
          <div className="accent-line"></div>
          <p className="body-large text-secondary" style={{ maxWidth: '800px', marginTop: '1.5rem' }}>
            Siamo a tua disposizione per qualsiasi informazione o preventivo personalizzato
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2 className="subsection-title">Informazioni di Contatto</h2>
              <div className="accent-line"></div>
              
              <div className="contact-methods">
                {/* Team Contacts */}
                {team.map((member) => (
                  <div key={member.id} className="contact-card card">
                    <div className="contact-card-header">
                      <h3 className="contact-name">{member.name}</h3>
                      <p className="contact-role">{member.role}</p>
                    </div>
                    <a href={`tel:${member.phone}`} className="contact-link">
                      <Phone size={20} className="contact-icon" />
                      <span>{member.phone}</span>
                    </a>
                    <a href={`mailto:${member.email}`} className="contact-link">
                      <Mail size={20} className="contact-icon" />
                      <span>{member.email}</span>
                    </a>
                  </div>
                ))}

                {/* Email */}
                <div className="contact-card card">
                  <div className="contact-card-header">
                    <h3 className="contact-name">Email</h3>
                    <p className="contact-role">Scrivici</p>
                  </div>
                  <a href={`mailto:${companyInfo.email}`} className="contact-link">
                    <Mail size={20} className="contact-icon" />
                    <span>{companyInfo.email}</span>
                  </a>
                </div>

                {/* Address */}
                <div className="contact-card card">
                  <div className="contact-card-header">
                    <h3 className="contact-name">Sede</h3>
                    <p className="contact-role">Via Monferrato, 20, 20098 San Giuliano Milanese MI</p>
                  </div>
                  <div className="contact-link">
                    <MapPin size={20} className="contact-icon" />
                    <span>{companyInfo.address}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2 className="subsection-title">Invia un Messaggio</h2>
              <div className="accent-line"></div>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nome e Cognome *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Telefono</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">Oggetto *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Messaggio *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    rows="6"
                    required
                  ></textarea>
                </div>

                <div className="privacy-info-box">
                  <p className="privacy-info-text">
                    📄 Informativa Privacy: <a href="https://privacy.sisaline.it" target="_blank" rel="noopener noreferrer" className="privacy-info-link">privacy.sisaline.it</a>
                  </p>
                </div>

                <div className="privacy-checkbox-wrapper">
                  <label className="privacy-checkbox-label">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="privacy-checkbox"
                      required
                    />
                    <span className="privacy-text">
                      Ho letto l'<a href="https://privacy.sisaline.it" target="_blank" rel="noopener noreferrer" className="privacy-link">informativa sulla privacy</a> e acconsento al trattamento dei miei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR).
                    </span>
                  </label>
                </div>

                <div className="privacy-checkbox-wrapper">
                  <label className="privacy-checkbox-label">
                    <input
                      type="checkbox"
                      checked={marketingAccepted}
                      onChange={(e) => setMarketingAccepted(e.target.checked)}
                      className="privacy-checkbox"
                    />
                    <span className="privacy-text">
                      Acconsento a ricevere comunicazioni commerciali e newsletter da SI.SA. LINE S.R.L.
                    </span>
                  </label>
                </div>

                {submitStatus && (
                  <div className={`form-status ${submitStatus.type === 'success' ? 'form-status-success' : 'form-status-error'}`}>
                    {submitStatus.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting || !privacyAccepted}
                  style={{ width: '100%', opacity: (!privacyAccepted && !isSubmitting) ? 0.6 : 1 }}
                >
                  {isSubmitting ? 'Invio in corso...' : (
                    <>
                      Invia Messaggio
                      <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .contatti-page {
          padding-top: 80px;
        }

        .page-hero {
          padding: var(--space-xl) 0 var(--space-lg);
          background: var(--bg-page);
        }

        /* Contact Grid */
        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-lg);
        }

        @media (max-width: 1024px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Contact Info */
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
        }

        .contact-methods {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
          margin-top: var(--space-sm);
        }

        .contact-card {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .contact-card-header {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .contact-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .contact-role {
          font-size: 0.875rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .contact-link {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--text-secondary);
          text-decoration: none;
          font-size: 1rem;
          transition: color 0.3s ease;
        }

        a.contact-link:hover {
          color: var(--brand-orange);
        }

        .contact-icon {
          color: var(--brand-orange);
          flex-shrink: 0;
        }

        /* Contact Form */
        .contact-form-wrapper {
          background: var(--bg-card);
          border: 2px solid var(--brand-blue);
          padding: var(--space-lg);
        }

        .contact-form {
          margin-top: var(--space-md);
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          color: var(--text-primary);
          font-size: 0.95rem;
          font-weight: 600;
        }

        .form-input,
        .form-textarea {
          background: var(--bg-page);
          border: 1px solid var(--border-light);
          color: var(--text-primary);
          padding: 0.875rem 1rem;
          font-family: 'Inter', sans-serif;
          font-size: 1rem;
          border-radius: 0.375rem;
          transition: all 0.3s ease;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--brand-blue);
          background: var(--bg-page);
        }

        .form-textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-status {
          padding: 1rem;
          border-radius: 0.375rem;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .form-status-success {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
          border: 1px solid rgba(34, 197, 94, 0.2);
        }

        .form-status-error {
          background: rgba(239, 68, 68, 0.1);

        .privacy-checkbox-wrapper {
          margin-top: 0.5rem;
        }

        .privacy-checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          cursor: pointer;
          user-select: none;
        }

        .privacy-info-box {
          background: rgba(255, 107, 53, 0.05);
          border: 1px solid rgba(255, 107, 53, 0.2);
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .privacy-info-text {
          color: var(--text-primary);
          font-size: 0.95rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .privacy-info-link {
          color: var(--brand-orange);
          font-weight: 700;
          text-decoration: underline;
          transition: all 0.3s ease;
        }

        .privacy-info-link:hover {
          color: var(--brand-blue);
          text-decoration: underline;
          transform: scale(1.02);
        }


        .privacy-checkbox {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          cursor: pointer;
          accent-color: var(--brand-orange);
        }

        .privacy-text {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .privacy-link {
          color: var(--brand-orange);
          text-decoration: underline;
          font-weight: 600;
          transition: color 0.3s ease;
        }

        .privacy-link:hover {
          color: var(--brand-blue);
        }

          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
      `}</style>
    </div>
  );
};

export default Contatti;
